import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, CheckCircle, Edit, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';

export const AdminUserPlansTab = ({ api, users, programs, workouts }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [viewWeek, setViewWeek] = useState(1);

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await api().get('/plans/admin/all');
      setPlans(res.data.plans || []);
    } catch (e) {
      console.debug('silent:', e.message);
    }
    setLoading(false);
  };

  const savePlanEdit = async (planId, weeks) => {
    try {
      await api().put(`/plans/admin/${planId}`, { weeks });
      toast.success('Plan updated');
      fetchPlans();
      setSelectedPlan(null);
    } catch {
      toast.error('Failed to save');
    }
  };

  const updateDay = (weekIdx, dayIdx, field, value) => {
    if (!selectedPlan) return;
    const updated = { ...selectedPlan, weeks: [...selectedPlan.weeks] };
    const week = { ...updated.weeks[weekIdx], days: [...updated.weeks[weekIdx].days] };
    week.days[dayIdx] = { ...week.days[dayIdx], [field]: value };
    updated.weeks[weekIdx] = week;
    setSelectedPlan(updated);
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" /></div>;

  if (selectedPlan) {
    const weekData = selectedPlan.weeks?.[viewWeek - 1];
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button variant="ghost" onClick={() => setSelectedPlan(null)} className="text-muted-foreground mb-2">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back to Plans
            </Button>
            <h3 className="font-heading text-xl text-white uppercase">{selectedPlan.user?.name}'s Plan</h3>
            <p className="text-muted-foreground text-sm">{selectedPlan.program_title} — {selectedPlan.duration_weeks} weeks</p>
            {selectedPlan.progress > 0 && (
              <p className="text-primary text-sm">{selectedPlan.progress}% complete ({selectedPlan.completed_days}/{selectedPlan.total_workout_days} days)</p>
            )}
          </div>
          <Button onClick={() => savePlanEdit(selectedPlan.id, selectedPlan.weeks)} className="bg-primary text-black">
            Save Changes
          </Button>
        </div>

        <div className="flex items-center justify-between mb-4 bg-card border border-border p-3">
          <button onClick={() => setViewWeek((w) => Math.max(1, w - 1))} disabled={viewWeek <= 1} className="text-white disabled:text-muted-foreground">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="font-heading text-primary uppercase text-sm">Week {viewWeek} — {weekData?.focus || ''}</span>
          <button
            onClick={() => setViewWeek((w) => Math.min(selectedPlan.duration_weeks, w + 1))}
            disabled={viewWeek >= selectedPlan.duration_weeks}
            className="text-white disabled:text-muted-foreground"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-2">
          {weekData?.days?.map((day, i) => (
            <div
              key={i}
              className={`p-3 border rounded ${
                day.completed ? 'border-green-500/30 bg-green-500/5' : day.type === 'rest' ? 'border-border bg-card/50' : 'border-primary/20 bg-card'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={`font-heading text-xs uppercase w-10 ${day.completed ? 'text-green-400' : 'text-muted-foreground'}`}>
                  {day.day?.slice(0, 3)}
                </span>
                <select
                  value={day.type}
                  onChange={(e) => updateDay(viewWeek - 1, i, 'type', e.target.value)}
                  className="bg-black text-white text-sm px-2 py-1 border border-border rounded"
                >
                  <option value="workout">Workout</option>
                  <option value="rest">Rest</option>
                </select>
                {day.type === 'workout' ? (
                  <select
                    value={day.workout_id || ''}
                    onChange={(e) => {
                      const w = workouts.find((wo) => wo.id === e.target.value);
                      updateDay(viewWeek - 1, i, 'workout_id', e.target.value);
                      updateDay(viewWeek - 1, i, 'workout_title', w?.title || '');
                    }}
                    className="flex-1 bg-black text-white text-sm px-2 py-1 border border-border rounded"
                  >
                    <option value="">-- Select Workout --</option>
                    {(Array.isArray(workouts) ? workouts : []).map((w) => (
                      <option key={w.id} value={w.id}>{w.title}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={day.notes || ''}
                    onChange={(e) => updateDay(viewWeek - 1, i, 'notes', e.target.value)}
                    className="flex-1 bg-black text-white text-sm px-2 py-1 border border-border rounded"
                    placeholder="Rest day notes..."
                  />
                )}
                {day.completed && <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">User Workout Plans</h3>

      {plans.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border">
          <p className="text-muted-foreground">No plans generated yet</p>
          <p className="text-muted-foreground text-sm mt-1">Plans are created when users purchase a program and generate their plan</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div key={plan.id} className="bg-card border border-border p-4" data-testid={`admin-plan-${plan.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">
                  {plan.user?.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{plan.user?.name || 'Unknown'}</p>
                  <p className="text-muted-foreground text-xs">{plan.user?.email}</p>
                </div>
              </div>
              <p className="text-primary font-heading text-sm uppercase">{plan.program_title}</p>
              <p className="text-muted-foreground text-xs mb-2">{plan.duration_weeks} weeks</p>
              {plan.progress > 0 && (
                <div className="mb-3">
                  <Progress value={plan.progress} className="h-1.5 bg-black" />
                  <p className="text-xs text-muted-foreground mt-1">{plan.progress}% complete</p>
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <Button size="sm" className="flex-1 bg-primary text-black" onClick={() => { setSelectedPlan(plan); setViewWeek(1); }}>
                  <Edit className="h-3 w-3 mr-1" /> View / Edit
                </Button>
              </div>
              {plan.edited_by_admin && <p className="text-xs text-yellow-400 mt-2">Edited by admin</p>}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};
